import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { getCashDesk, saveCashPayment, cashNotReceived, markCashDeposited, getCashOnHand } from "@/lib/api";
import { fmtMoneyWith } from "@/lib/money";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Banknote, RefreshCw } from "lucide-react";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "—");
const held = (h) => (h < 1 ? "under an hour" : h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} days`);

/** Cash at the front desk, through the member app: the member picks cash, the staff member who took it
 *  types their staff ID on the member's phone, then saves it here. The cash is then logged as held by
 *  them until they mark it deposited. Master admins see every gym's cash on hand at the top. */
export default function CashPayments() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [all, setAll] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await getCashDesk();
      setData(d);
      if (d.is_master_admin) setAll(await getCashOnHand());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cash payments");
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    } finally {
      setBusy(null);
    }
  };

  const [cardType, setCardType] = useState({});   // order id -> "Credit Card" | "Debit Card"
  const save = (w) => act(w.order_id, () => saveCashPayment(w.order_id, w.method === "card" ? (cardType[w.order_id] || "Credit Card") : null),
    w.method === "card" ? `Saved. ${w.name}'s payment is in All Sales.` : `Saved. ${w.name}'s payment is in All Sales and the cash is logged with ${w.staff_name}.`);
  const notReceived = (w) => {
    if (!window.confirm(`No cash was taken from ${w.name}? Their renewal is cancelled and any points go back.`)) return;
    act(w.order_id, () => cashNotReceived(w.order_id), "Cancelled. Points returned.");
  };
  const deposited = (h) => {
    const ref = window.prompt(`Deposited ${fmtMoneyWith(h.currency_symbol, h.amount)}? Bank slip or reference (optional):`, "");
    if (ref === null) return;
    act(h.id, () => markCashDeposited(h.id, ref), "Marked deposited.");
  };

  if (!data) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="cash-payments">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="eyebrow">Retention · Payments</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Cash Payments</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Members pay cash through the app: they pick &quot;Pay cash at the desk&quot;, you take the cash and type your staff
            ID on their phone, then press Save here. The cash then shows as held by you until you mark it deposited.
            {data.my_employee_id
              ? <> Your staff ID is <span className="font-semibold text-zinc-200">{data.my_employee_id}</span>.</>
              : <span className="text-amber-300"> You don&apos;t have a staff ID yet. Ask a master admin to check User Management.</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" />Refresh</Button>
      </div>

      {all && (
        <section className="card-surface p-5 space-y-3" data-testid="cash-on-hand">
          <div className="flex items-baseline justify-between">
            <h3 className="font-semibold">Cash on hand, all gyms</h3>
            <span className={`text-xs ${all.late ? "text-rose-300" : "text-zinc-500"}`}>
              {all.count ? `${all.count} not yet deposited${all.late ? ` · ${all.late} held over 2 days` : ""}` : "Everything is deposited."}
            </span>
          </div>
          {all.gyms.map((g) => (
            <div key={g.facility_id} className="rounded-lg border border-[#1f1f1f] p-3">
              <div className="flex justify-between text-sm">
                <span className="font-semibold">{g.gym_name}</span>
                <span className="font-semibold">{fmtMoneyWith(g.currency_symbol, g.total)}</span>
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-400">
                {g.rows.map((h) => (
                  <li key={h.id} className={h.late ? "text-rose-300" : ""}>
                    {h.staff_name} is holding {fmtMoneyWith(h.currency_symbol, h.amount)} from {h.member_name} ({h.programme}), for {held(h.hours)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      )}

      <section className="space-y-2">
        <h3 className="font-semibold">Waiting to be saved</h3>
        {data.waiting.length === 0 ? (
          <div className="card-surface p-6 text-sm text-zinc-500">Nothing waiting.</div>
        ) : data.waiting.map((w) => (
          <div key={w.order_id} className="card-surface p-4 flex flex-wrap items-center gap-4">
            <Banknote className="h-5 w-5 text-amber-300" />
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold">{w.name} · {fmtMoneyWith(w.currency_symbol, w.amount)} {w.method === "card" ? "by card" : "cash"}</div>
              <div className="text-xs text-zinc-500">{w.programme} · taken by {w.staff_name} at {when(w.claimed_at)}</div>
            </div>
            {w.mine || data.is_master_admin ? (
              <div className="flex gap-2">
                {w.method === "card" && (
                  <select value={cardType[w.order_id] || "Credit Card"} onChange={(e) => setCardType((c) => ({ ...c, [w.order_id]: e.target.value }))}
                    aria-label="Card type" className="rounded border border-[#262626] bg-[#0a0a0a] px-2 text-xs text-zinc-300">
                    <option>Credit Card</option><option>Debit Card</option>
                  </select>
                )}
                <Button size="sm" disabled={busy === w.order_id} onClick={() => save(w)}>Save</Button>
                <Button size="sm" variant="ghost" disabled={busy === w.order_id} onClick={() => notReceived(w)}>Not received</Button>
              </div>
            ) : <span className="text-xs text-zinc-500">Only {w.staff_name} can save this</span>}
          </div>
        ))}
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">Cash held, not yet deposited</h3>
        {data.held.length === 0 ? (
          <div className="card-surface p-6 text-sm text-zinc-500">No cash being held at this gym.</div>
        ) : (
          <div className="card-surface overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <tr className="border-b border-[#1c1c1c]">
                  <th className="px-4 py-3">Held by</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">Since</th><th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.held.map((h) => (
                  <tr key={h.id} className="border-b border-[#161616]">
                    <td className="px-4 py-3 font-semibold">{h.staff_name}</td>
                    <td className="px-4 py-3">{fmtMoneyWith(h.currency_symbol, h.amount)}</td>
                    <td className="px-4 py-3 text-zinc-400">{h.member_name} · {h.programme}</td>
                    <td className={`px-4 py-3 ${h.late ? "text-rose-300" : "text-zinc-400"}`}>{when(h.received_at)} ({held(h.hours)})</td>
                    <td className="px-4 py-3 text-right">
                      {(h.mine || data.is_master_admin) && (
                        <Button size="sm" variant="outline" disabled={busy === h.id} onClick={() => deposited(h)}>Deposited</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data.deposited.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-semibold text-zinc-400">Recently deposited</h3>
          <ul className="card-surface p-4 space-y-1 text-xs text-zinc-500">
            {data.deposited.map((h) => (
              <li key={h.id}>
                {fmtMoneyWith(h.currency_symbol, h.amount)} from {h.member_name}, held by {h.staff_name}, deposited {when(h.deposited_at)}
                {h.deposited_by && h.deposited_by !== h.staff_name ? ` by ${h.deposited_by}` : ""}{h.reference ? ` · ref ${h.reference}` : ""}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
